import React from "react";
import { Card, CardContent } from "../ui/card";
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  ClipboardCheck,
  LineChartIcon,
} from "lucide-react";
import { useTradeJournal } from "../../context/TradeJournalContext";

const TradingJournalStats = () => {
  const { stats, timeFilter } = useTradeJournal();

  // Add console logs to debug stats values
  console.log("Stats in TradingJournalStats:", stats);

  // Helper function to get the time period label
  const getTimePeriodLabel = () => {
    switch (timeFilter) {
      case "day":
        return "Today";
      case "week":
        return "This Week";
      case "month":
        return "This Month";
      case "year":
        return "This Year";
      case "all":
        return "All Time";
      case "custom":
        return "Selected Period";
      default:
        return "Selected Period";
    }
  };

  // Calculate average trade value
  const averageTrade = stats?.totalTrades
    ? ((stats?.netProfit || 0) / stats.totalTrades).toFixed(2)
    : "0.00";

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-xl font-semibold flex items-center">
          Performance Statistics
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="intellectia-card bg-white/5">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm mb-1">Total P&L</p>
                <p
                  className={`text-2xl font-bold ${
                    stats?.netProfit >= 0 ? "text-green-400" : "text-red-400"
                  }`}
                >
                  ₹
                  {stats?.netProfit?.toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                  }) || "0.00"}
                </p>
              </div>
              <div
                className={`p-3 rounded-full ${
                  stats?.netProfit >= 0
                    ? "bg-market-teal/10"
                    : "bg-darkMode-red/10"
                }`}
              >
                {stats?.netProfit >= 0 ? (
                  <TrendingUp className="h-6 w-6 text-market-teal" />
                ) : (
                  <TrendingDown className="h-6 w-6 text-darkMode-red" />
                )}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-2">
              <div>
                <p className="text-white/40 text-xs">Profit</p>
                <p className="text-green-400">
                  ₹
                  {stats?.totalProfit?.toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                  }) || "0.00"}
                </p>
              </div>
              <div>
                <p className="text-white/40 text-xs">Loss</p>
                <p className="text-red-400">
                  ₹
                  {stats?.totalLoss?.toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                  }) || "0.00"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="intellectia-card bg-white/5">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm mb-1">
                  Win Rate ({getTimePeriodLabel()})
                </p>
                <p className="text-2xl font-bold text-market-teal">
                  {(stats?.winRate || 0).toFixed(1)}%
                </p>
              </div>
              <div className="p-3 rounded-full bg-market-teal/10">
                <BarChart3 className="h-6 w-6 text-market-teal" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-2">
              <div>
                <p className="text-white/40 text-xs">Win</p>
                <p className="text-white/80">
                  {stats?.profitTrades || 0} trades
                </p>
              </div>
              <div>
                <p className="text-white/40 text-xs">Loss</p>
                <p className="text-white/80">{stats?.lossTrades || 0} trades</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="intellectia-card bg-white/5">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm mb-1">Average Trade</p>
                <p
                  className={`text-2xl font-bold ${
                    Number(averageTrade) >= 0
                      ? "text-green-400"
                      : "text-red-400"
                  }`}
                >
                  ₹{averageTrade}
                </p>
              </div>
              <div className="p-3 rounded-full bg-market-teal/10">
                <ClipboardCheck className="h-6 w-6 text-market-teal" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-2">
              <div>
                <p className="text-white/40 text-xs">Avg Win</p>
                <p className="text-green-400">
                  ₹{stats?.avgWin?.toFixed(2) || "0.00"}
                </p>
              </div>
              <div>
                <p className="text-white/40 text-xs">Avg Loss</p>
                <p className="text-red-400">
                  ₹{stats?.avgLoss?.toFixed(2) || "0.00"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="intellectia-card bg-white/5">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm mb-1">Profit Factor</p>
                <p className="text-2xl font-bold text-market-teal">
                  {stats?.profitFactor
                    ? stats.profitFactor === Infinity
                      ? "∞"
                      : stats.profitFactor.toFixed(1)
                    : "0.0"}
                  x
                </p>
              </div>
              <div className="p-3 rounded-full bg-market-teal/10">
                <LineChartIcon className="h-6 w-6 text-market-teal" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-2">
              <div>
                <p className="text-white/40 text-xs">Best Trade</p>
                <p className="text-green-400">
                  ₹{stats?.largestWin?.toLocaleString("en-IN") || "0.00"}
                </p>
              </div>
              <div>
                <p className="text-white/40 text-xs">Worst Trade</p>
                <p className="text-red-400">
                  ₹{stats?.largestLoss?.toLocaleString("en-IN") || "0.00"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TradingJournalStats;
